<template>
  <section class="payment-schedule" data-cy="payment-schedule">
    <div class="sub-header-text">PAYMENT SCHEDULE</div>
    <Table v-if="installments.length">
      <thead>
        <tr>
          <th class="col-position">#</th>
          <th class="text-left">Installment</th>
          <th class="text-right">Draw Amount</th>
          <th class="text-right">%</th>
          <th class="text-right">Due Date</th>
          <th class="text-right">Payment Status</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(installment, index) in installments"
          :key="installment.id ?? installment.position"
          data-cy="payment-schedule-row"
        >
          <td class="col-position">{{ installment.position + 1 }}</td>
          <td class="text-left">{{ installment.label }}</td>
          <td class="text-right">
            {{ currency(dollarCentsFor(installment) / 100) }}
          </td>
          <td class="text-right">
            {{ percentageFor(installment).toFixed(2) }}%
          </td>
          <td class="text-right">
            {{ formatDueDate(installment.due_date) }}
          </td>
          <td class="text-right">
            <Badge
              :text="statusTextFor(index)"
              :color="statusColorFor(index)"
              variant="chip"
              :data-cy="`payment-schedule-status-${index}`"
            />
          </td>
        </tr>
      </tbody>
    </Table>
  </section>
</template>

<script setup lang="ts">
import currency from "serviceshift-ui/shared/src/filters/currency";
import { computed } from "vue";

import Badge from "@/components/Badge.vue";
import Table from "@/serviceshift-ui/components/General/Table.vue";
import type {
  Installment,
  JobPaymentSchedule
} from "@/vuex/modules/jobPaymentSchedule";

const props = defineProps<{
  schedule: JobPaymentSchedule;
}>();

const installments = computed<Installment[]>(
  () => props.schedule?.installments ?? []
);

// Rails serializes decimal columns as strings to preserve precision, so the
// raw fields arrive as strings (e.g. "50.0"). Coerce before arithmetic /
// .toFixed(), or rendering blows up silently in production.
const dollarCentsFor = (installment: Installment): number =>
  Number(
    props.schedule.split_mode === "amount"
      ? installment.value
      : installment.calculated_opposite_value
  );

const percentageFor = (installment: Installment): number =>
  Number(
    props.schedule.split_mode === "percentage"
      ? installment.value
      : installment.calculated_opposite_value
  );

const statusTextFor = (index: number): string =>
  index === 0 ? "Current Draw" : "Scheduled";

const statusColorFor = (index: number): string =>
  index === 0 ? "yellow" : "gray";

// Date-only strings (e.g. "2026-05-15") parse as UTC midnight, so format in
// UTC to avoid the column shifting back a day in negative timezones.
const formatDueDate = (dueDate?: string | null): string => {
  if (!dueDate) return "—";
  const parsed = new Date(dueDate);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC"
  });
};
</script>

<style lang="scss" scoped>

.sub-header-text {
  font-size: 14px;
  font-weight: bolder;
  line-height: 16px;
  margin-bottom: 14px;
}

.payment-schedule {
  margin-top: 16px;

  .col-position {
    width: 40px;
    text-align: center;
  }
}
</style>
